/**
 * ログインページコンポーネント
 * SNS認証（Google/LINE）+ サイトパスワードゲート（プレオープン時）
 *
 * フロー:
 *   Step 1: サイトパスワード入力（SITE_PASSWORD設定時のみ）
 *   Step 2: SNS認証（Google/LINE ボタン）
 *
 * デザイン: Babak LP（C+案 / エディトリアル明朝 × うさぎの世界）の「入口」。
 *   森の小道のフルブリード背景に、クリームのフロストパネルを重ねる。
 *   感情語は Noto Serif JP（明朝）、機能（ボタン/入力）は Noto Sans JP。
 *   トークンは styles/babak-lp.css と同系（Login.css に自己完結で定義）。
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Login.css';

/**
 * 共通シェル: 森の背景 + 暗幕 + 中央のフロストパネル
 */
function LoginShell({ children }) {
  return (
    <div className="login-screen">
      <div
        className="login-bg"
        aria-hidden="true"
        style={{ backgroundImage: "url('/lp/illustration-forest-path.png')" }}
      />
      <div className="login-bg-overlay" aria-hidden="true" />
      <div className="login-panel">{children}</div>
    </div>
  );
}

/**
 * パネル上部のブランド（うさぎマーク + eyebrow + 明朝タイトル + lede）
 */
function LoginBrand({ title, lede }) {
  return (
    <div className="login-brand">
      <img
        src="/lp/babak-rabbit.png"
        alt="Babak"
        className="login-mark"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = '/favicon.svg';
        }}
      />
      <div className="login-eyebrow">Babak</div>
      {title && <h1 className="login-title">{title}</h1>}
      {lede && <p className="login-lede">{lede}</p>}
    </div>
  );
}

/**
 * @param {object}   props
 * @param {function} [props.onLoginSuccess] SNS ログイン成立時（mode='full'）
 * @param {'full'|'siteGate'} [props.mode='full']
 *   'full'    : サイトパスワード → SNS ログインまで（/chat・/admin の入口）
 *   'siteGate': サイトパスワード（babak2026）だけのゲート。通過したら onSiteUnlock を呼ぶ。
 *               公開ブラウズ（トップ / ・/about）をプレオープン中だけ閉じる用途。SNS は求めない。
 * @param {function} [props.onSiteUnlock] mode='siteGate' でゲート通過時に呼ばれる
 */
export default function LoginPage({ onLoginSuccess, mode = 'full', onSiteUnlock }) {
  const [sitePassword, setSitePassword] = useState('');
  const [sitePasswordRequired, setSitePasswordRequired] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [connectionError, setConnectionError] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // 初期化: サイトパスワード要否を確認
  useEffect(() => {
    const checkAuthMode = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/auth/me`, {
          credentials: 'include'
        });
        const data = await response.json();

        // サイトパスワードが必要な場合
        if (data.sitePasswordRequired) {
          setSitePasswordRequired(true);
          setIsInitializing(false);
          return; // サイトパスワードが先
        }

        // サイトパスワード不要 or 検証済み
        // サイトゲート用途（公開ブラウズ）では SNS を求めずゲートを開く
        if (mode === 'siteGate') {
          onSiteUnlock?.();
          return; // 親が children へ切替（このまま読み込み中表示のままアンマウント）
        }

        // フル用途（/chat 等）: SNS認証済みならリダイレクト
        if (data.authenticated && data.user) {
          onLoginSuccess(data.user);
        }

        setConnectionError(false);
      } catch (err) {
        // バックエンド未起動時のネットワークエラー
        if (err.name === 'TypeError') {
          setConnectionError(true);
        } else {
          console.error('Auth mode check error:', err);
          setConnectionError(true);
        }
      }
      setIsInitializing(false);
    };

    checkAuthMode();
  }, [backendUrl, onLoginSuccess, mode, onSiteUnlock]);

  // 接続リトライ
  const handleRetry = () => {
    setConnectionError(false);
    setIsInitializing(true);
    setError('');
    // useEffect の依存を変えずに再実行するため、直接呼び出し
    const retry = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/auth/me`, {
          credentials: 'include'
        });
        const data = await response.json();

        if (data.sitePasswordRequired) {
          setSitePasswordRequired(true);
          setConnectionError(false);
          setIsInitializing(false);
          return;
        }

        if (mode === 'siteGate') {
          onSiteUnlock?.();
          return;
        }

        if (data.authenticated && data.user) {
          onLoginSuccess(data.user);
        }

        setConnectionError(false);
      } catch (err) {
        setConnectionError(true);
      }
      setIsInitializing(false);
    };
    retry();
  };

  // サイトパスワード検証
  const handleSitePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${backendUrl}/api/auth/site-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',  // Cookie受信のために必要
        body: JSON.stringify({ password: sitePassword })
      });

      const data = await response.json();

      if (data.success) {
        setSitePassword('');
        // サイトゲート用途は SNS を求めずゲートを開く
        if (mode === 'siteGate') {
          onSiteUnlock?.();
          return;
        }
        setSitePasswordRequired(false);
        // サイトパスワード通過 → SNSログインボタン表示へ
      } else {
        setError(data.error || 'サイトパスワードが正しくありません');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      console.error('Site password verify error:', err);
      setError('サーバーとの通信に失敗しました');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Google認証（現在のオリジンをリダイレクト先として渡す）
  const handleGoogleLogin = () => {
    const redirectUri = encodeURIComponent(window.location.origin);
    window.location.href = `${backendUrl}/api/auth/google?redirect_uri=${redirectUri}`;
  };

  // LINE認証（現在のオリジンをリダイレクト先として渡す）
  const handleLineLogin = () => {
    const redirectUri = encodeURIComponent(window.location.origin);
    window.location.href = `${backendUrl}/api/auth/line?redirect_uri=${redirectUri}`;
  };

  // 初期化中
  if (isInitializing) {
    return (
      <LoginShell>
        <div className="login-loading">読み込み中…</div>
      </LoginShell>
    );
  }

  // 接続エラー
  if (connectionError) {
    return (
      <LoginShell>
        <LoginBrand
          title="すこし、つながりません。"
          lede="サーバーに接続できませんでした。少し待ってから、もう一度お試しください。"
        />
        <div className="login-actions">
          <div className="login-error" role="alert">サーバーに接続できません</div>
          <button onClick={handleRetry} className="login-btn login-btn-primary">
            再試行
          </button>
        </div>
      </LoginShell>
    );
  }

  // サイトパスワードステップ
  if (sitePasswordRequired) {
    return (
      <LoginShell>
        <LoginBrand
          title="そっと、扉をひらく。"
          lede="このサイトはいま、限られた方だけに開かれています。サイトパスワードを入力してください。"
        />
        <form onSubmit={handleSitePasswordSubmit} className="login-actions">
          {error && (
            <div className="login-error" role="alert">{error}</div>
          )}
          <input
            type="password"
            value={sitePassword}
            onChange={(e) => setSitePassword(e.target.value)}
            className="login-input"
            placeholder="サイトパスワードを入力"
            required
            autoFocus
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading || !sitePassword}
            className="login-btn login-btn-primary"
          >
            {loading ? '確認中…' : '確認'}
          </button>
        </form>
        <div className="login-foot">その景色は、あなただけの問いになる。</div>
      </LoginShell>
    );
  }

  // サイトゲート用途では SNS ステップを描画しない
  // （onSiteUnlock 済みで親が children に切替わるまでの一瞬のフォールバック）
  if (mode === 'siteGate') {
    return (
      <LoginShell>
        <div className="login-loading">読み込み中…</div>
      </LoginShell>
    );
  }

  // SNS認証（Google/LINE）
  return (
    <LoginShell>
      <LoginBrand
        title={<>Babak the Worldへ、<br />ようこそ。</>}
        lede="ログインして、あなたの問いのつづきへ。"
      />
      <div className="login-actions">
        {error && (
          <div className="login-error" role="alert">{error}</div>
        )}

        <button
          onClick={handleGoogleLogin}
          className="login-btn login-btn-google"
          disabled={loading}
        >
          <svg className="login-btn-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Googleでログイン
        </button>

        <button
          onClick={handleLineLogin}
          className="login-btn login-btn-line"
          disabled={loading}
        >
          <svg className="login-btn-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
            <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
          </svg>
          LINEでログイン
        </button>
      </div>
      <nav className="login-legal">
        <Link to="/terms">利用規約</Link>
        <span aria-hidden="true">·</span>
        <Link to="/privacy">プライバシーポリシー</Link>
      </nav>
      <div className="login-foot">その景色は、あなただけの問いになる。</div>
    </LoginShell>
  );
}
